import { CLASS_LOG_MODULES, CLASS_LOG_MODULE_TYPES } from '../constants/classLogModules';
import { normalizeModuleLevel } from '../constants/classLogModuleLevels';

const MODULE_LABELS = Object.fromEntries(CLASS_LOG_MODULES.map((m) => [m.type, m.label]));

export const LMS_ASSIGNMENT_REQUIRED_MESSAGE =
  'Ask your supervisor to assign at least one module and activity before you can fill the class log.';

export const LMS_ASSIGNMENT_REQUIRED_PLAN_MESSAGE =
  'Ask your supervisor to assign at least one module and activity before you can plan the next class.';

export function emptyClassLogJson() {
  return { version: 1, coveredModules: [], miscRemarks: '' };
}

export function emptyNextPlanJson() {
  return { version: 1, plannedModules: [], legacyText: null };
}

export function emptyClassLogJsonV2() {
  return { version: 2, coveredModules: [], miscRemarks: '' };
}

export function emptyNextPlanJsonV2() {
  return { version: 2, plannedModules: [], legacyText: null };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function isUuid(v) {
  return !!(v && typeof v === 'string' && UUID_RE.test(v));
}

/** True when value has real content (letters/digits). Allows "meditation module 1"; rejects "" or "   ". */
function hasNonEmptyText(value) {
  const s = String(value ?? '').trim();
  if (!s) return false;
  return /[\p{L}\p{N}]/u.test(s);
}

export function lmsActivityIdsFromEntry(m) {
  if (!m) return [];
  if (Array.isArray(m.activity_ids)) {
    return m.activity_ids.filter((id) => isUuid(id)).map(String);
  }
  if (isUuid(m.activity_id)) return [String(m.activity_id)];
  return [];
}

function lmsActivityNamesFromEntry(m, activityIds) {
  if (Array.isArray(m.activity_names)) {
    const names = m.activity_names.map((n) => (hasNonEmptyText(n) ? String(n).trim() : ''));
    return activityIds.map((_, i) => names[i] || '');
  }
  if (hasNonEmptyText(m.activity_name) && activityIds.length === 1) {
    return [String(m.activity_name).trim()];
  }
  return activityIds.map(() => '');
}

function mapModuleEntryRaw(m) {
  return {
    type: m.type,
    level: normalizeModuleLevel(m.level),
    notes: m.notes != null ? String(m.notes) : '',
  };
}

function mapModuleEntryForStorage(m) {
  return {
    type: m.type,
    level: normalizeModuleLevel(m.level),
    notes: hasNonEmptyText(m.notes) ? String(m.notes).trim() : '',
  };
}

function mapLmsEntryForStorage(m) {
  const activity_ids = lmsActivityIdsFromEntry(m);
  if (!m?.module_id || !activity_ids.length) return null;
  const activity_names = lmsActivityNamesFromEntry(m, activity_ids).map((n) => (n ? n : null));
  const entry = {
    module_id: String(m.module_id),
    activity_ids,
    level: normalizeModuleLevel(m.level),
  };
  if (hasNonEmptyText(m.module_name)) {
    entry.module_name = String(m.module_name).trim();
  }
  if (activity_names.some(Boolean)) {
    entry.activity_names = activity_names.map((n) => n || '');
  }
  return entry;
}

/** Keep only modules that have non-empty notes (level alone is not enough). */
function filterModulesWithNotes(modules) {
  return (modules || []).filter((m) => m && hasNonEmptyText(m.notes));
}

function filterLmsModulesWithActivities(modules) {
  return (modules || []).map(mapLmsEntryForStorage).filter(Boolean);
}

/** Preserve exact typing (including spaces) for controlled form fields. */
export function parseClassLogForForm(raw) {
  if (raw && typeof raw === 'object') {
    if (Number(raw.version) === 2) {
      const coveredModules = Array.isArray(raw.coveredModules) ? raw.coveredModules : [];
      return {
        version: 2,
        coveredModules: coveredModules
          .filter((m) => m && m.module_id)
          .map((m) => {
            const activity_ids = lmsActivityIdsFromEntry(m);
            return {
              module_id: String(m.module_id),
              activity_ids,
              module_name: m.module_name != null ? String(m.module_name) : '',
              activity_names: lmsActivityNamesFromEntry(m, activity_ids),
              level: normalizeModuleLevel(m.level),
            };
          }),
        miscRemarks: raw.miscRemarks != null ? String(raw.miscRemarks) : '',
      };
    }
    const coveredModules = Array.isArray(raw.coveredModules)
      ? raw.coveredModules
          .filter((m) => m && CLASS_LOG_MODULE_TYPES.includes(m.type))
          .map(mapModuleEntryRaw)
      : [];
    return {
      version: 1,
      coveredModules,
      miscRemarks: raw.miscRemarks != null ? String(raw.miscRemarks) : '',
    };
  }
  return emptyClassLogJson();
}

export function parseNextPlanForForm(raw) {
  if (raw && typeof raw === 'object') {
    if (Number(raw.version) === 2) {
      const plannedModules = Array.isArray(raw.plannedModules) ? raw.plannedModules : [];
      const legacyText = raw.legacyText != null ? String(raw.legacyText) : '';
      return {
        version: 2,
        plannedModules: plannedModules
          .filter((m) => m && m.module_id)
          .map((m) => {
            const activity_ids = lmsActivityIdsFromEntry(m);
            return {
              module_id: String(m.module_id),
              activity_ids,
              module_name: m.module_name != null ? String(m.module_name) : '',
              activity_names: lmsActivityNamesFromEntry(m, activity_ids),
              level: normalizeModuleLevel(m.level),
            };
          }),
        legacyText: legacyText || null,
      };
    }
    const plannedModules = Array.isArray(raw.plannedModules)
      ? raw.plannedModules
          .filter((m) => m && CLASS_LOG_MODULE_TYPES.includes(m.type))
          .map(mapModuleEntryRaw)
      : [];
    const legacyText = raw.legacyText != null ? String(raw.legacyText) : '';
    return { version: 1, plannedModules, legacyText: legacyText || null };
  }
  return emptyNextPlanJson();
}

export function normalizeClassLogJson(raw, legacyBatchNotes) {
  if (raw && typeof raw === 'object') {
    if (Number(raw.version) === 2) {
      const coveredModules = filterLmsModulesWithActivities(
        Array.isArray(raw.coveredModules) ? raw.coveredModules : []
      );
      return {
        version: 2,
        coveredModules,
        miscRemarks: hasNonEmptyText(raw.miscRemarks) ? String(raw.miscRemarks).trim() : '',
      };
    }
    const coveredModules = filterModulesWithNotes(
      Array.isArray(raw.coveredModules)
        ? raw.coveredModules
            .filter((m) => m && CLASS_LOG_MODULE_TYPES.includes(m.type))
            .map(mapModuleEntryForStorage)
        : []
    );
    return {
      version: 1,
      coveredModules,
      miscRemarks: hasNonEmptyText(raw.miscRemarks) ? String(raw.miscRemarks).trim() : '',
    };
  }
  const legacy = String(legacyBatchNotes || '').trim();
  if (legacy) {
    return { version: 1, coveredModules: [], miscRemarks: legacy };
  }
  return emptyClassLogJson();
}

export function normalizeNextPlanJson(raw, legacyWeeklyPlan) {
  if (raw && typeof raw === 'object') {
    if (Number(raw.version) === 2) {
      const plannedModules = filterLmsModulesWithActivities(
        Array.isArray(raw.plannedModules) ? raw.plannedModules : []
      );
      const legacyText = hasNonEmptyText(raw.legacyText) ? String(raw.legacyText).trim() : '';
      return { version: 2, plannedModules, legacyText: legacyText || null };
    }
    const plannedModules = filterModulesWithNotes(
      Array.isArray(raw.plannedModules)
        ? raw.plannedModules
            .filter((m) => m && CLASS_LOG_MODULE_TYPES.includes(m.type))
            .map(mapModuleEntryForStorage)
        : []
    );
    const legacyText = hasNonEmptyText(raw.legacyText) ? String(raw.legacyText).trim() : '';
    return { version: 1, plannedModules, legacyText: legacyText || null };
  }
  const legacy = String(legacyWeeklyPlan || '').trim();
  if (legacy) {
    return { version: 1, plannedModules: [], legacyText: legacy };
  }
  return emptyNextPlanJson();
}

export function parseSessionLog(sessionLog) {
  if (!sessionLog) {
    return { classLogJson: emptyClassLogJson(), nextPlanJson: emptyNextPlanJson() };
  }
  const rawClass = sessionLog.classLogJson || sessionLog.class_log_json;
  const rawPlan = sessionLog.nextPlanJson || sessionLog.next_plan_json;
  return {
    classLogJson:
      rawClass != null
        ? parseClassLogForForm(rawClass)
        : normalizeClassLogJson(rawClass, sessionLog.batch_notes),
    nextPlanJson:
      rawPlan != null
        ? parseNextPlanForForm(rawPlan)
        : normalizeNextPlanJson(rawPlan, sessionLog.weekly_plan),
  };
}

export function isClassLogComplete(classLogJson, legacyBatchNotes) {
  const normalized = normalizeClassLogJson(classLogJson, legacyBatchNotes);
  if (normalized.coveredModules.length > 0) return true;
  return hasNonEmptyText(normalized.miscRemarks);
}

/** True when this session already has a class log saved, including older notes stored as batch notes. */
export function classLogAlreadySaved(sessionLog) {
  if (!sessionLog) return false;
  const raw = sessionLog.classLogJson ?? sessionLog.class_log_json;
  if (isClassLogComplete(raw, sessionLog.batch_notes)) return true;
  if (hasNonEmptyText(sessionLog.batch_notes)) return true;
  const parsed = parseClassLogForForm(raw);
  if ((parsed?.coveredModules || []).length > 0) return true;
  return hasNonEmptyText(parsed?.miscRemarks);
}

export function hasNextPlan(nextPlanJson, legacyWeeklyPlan) {
  const normalized = normalizeNextPlanJson(nextPlanJson, legacyWeeklyPlan);
  if (normalized.plannedModules.length > 0) return true;
  return hasNonEmptyText(normalized.legacyText);
}

/**
 * Unsaved class-log draft from the previous session's next-class plan.
 * Only version-2 plans. Modules and activities must still be assigned to the teacher.
 * Returns null when there is nothing safe to tick.
 */
export function buildClassLogDraftFromPreviousPlan(previousClassPlan, lmsModules) {
  if (!previousClassPlan?.hasPlan) return null;
  const plan = previousClassPlan.nextPlanJson;
  if (!plan || Number(plan.version) !== 2) return null;
  const planned = Array.isArray(plan.plannedModules) ? plan.plannedModules : [];
  if (!planned.length) return null;

  const allowedByModule = new Map();
  (lmsModules || []).forEach((mod) => {
    const moduleId = String(mod?.moduleId || mod?.module_id || '');
    if (!moduleId) return;
    const ids = new Set(
      (mod.activities || [])
        .map((a) => String(a?.activityId || a?.activity_id || ''))
        .filter(Boolean)
    );
    allowedByModule.set(moduleId, ids);
  });

  const coveredModules = [];
  for (const entry of planned) {
    const moduleId = String(entry?.module_id || '');
    const allowed = allowedByModule.get(moduleId);
    if (!allowed) continue;
    const activity_ids = lmsActivityIdsFromEntry(entry).filter((id) => allowed.has(id));
    if (!activity_ids.length) continue;
    coveredModules.push({
      module_id: moduleId,
      activity_ids,
      level: normalizeModuleLevel(entry.level),
    });
  }
  if (!coveredModules.length) return null;
  return { version: 2, coveredModules, miscRemarks: '' };
}

export function buildClassLogPayload(checkedTypes, moduleFields, miscRemarks) {
  const coveredModules = [...checkedTypes].map((type) => ({
    type,
    level: normalizeModuleLevel(moduleFields[type]?.level),
    notes: moduleFields[type]?.notes ?? '',
  }));
  return {
    version: 1,
    coveredModules,
    miscRemarks: String(miscRemarks ?? ''),
  };
}

export function buildNextPlanPayload(checkedTypes, moduleFields, legacyText) {
  const plannedModules = [...checkedTypes].map((type) => ({
    type,
    level: normalizeModuleLevel(moduleFields[type]?.level),
    notes: moduleFields[type]?.notes ?? '',
  }));
  return {
    version: 1,
    plannedModules,
    legacyText: legacyText != null ? String(legacyText) : null,
  };
}

export function buildClassLogPayloadV2(selectedModuleIds, moduleFields, miscRemarks) {
  const coveredModules = [...selectedModuleIds].map((moduleId) => ({
    module_id: moduleId,
    activity_ids: [...(moduleFields[moduleId]?.activityIds || [])],
    level: normalizeModuleLevel(moduleFields[moduleId]?.level),
  }));
  return {
    version: 2,
    coveredModules,
    miscRemarks: String(miscRemarks ?? ''),
  };
}

export function buildNextPlanPayloadV2(selectedModuleIds, moduleFields, legacyText) {
  const plannedModules = [...selectedModuleIds].map((moduleId) => ({
    module_id: moduleId,
    activity_ids: [...(moduleFields[moduleId]?.activityIds || [])],
    level: normalizeModuleLevel(moduleFields[moduleId]?.level),
  }));
  return {
    version: 2,
    plannedModules,
    legacyText: legacyText != null ? String(legacyText) : null,
  };
}

export function classLogFromChecked(checkedTypes, moduleFields, miscRemarks) {
  return buildClassLogPayload(checkedTypes, moduleFields, miscRemarks);
}

export function moduleFieldsFromLog(modules = []) {
  const fields = {};
  modules.forEach((m) => {
    if (m && m.module_id) {
      fields[String(m.module_id)] = {
        level: normalizeModuleLevel(m.level),
        activityIds: lmsActivityIdsFromEntry(m),
      };
      return;
    }
    fields[m.type] = { level: normalizeModuleLevel(m.level), notes: m.notes || '' };
  });
  return fields;
}

export function checkedTypesFromLog(modules = []) {
  return new Set(modules.map((m) => (m && m.module_id ? String(m.module_id) : m.type)));
}

export function getUncheckedModulesWithEmptyNotes(checkedTypes, moduleFields) {
  return [...checkedTypes].filter((type) => !hasNonEmptyText(moduleFields[type]?.notes));
}

export function getLmsModulesMissingActivities(checkedModuleIds, moduleFields) {
  return [...checkedModuleIds].filter((id) => !(moduleFields[id]?.activityIds || []).length);
}

/** Selected modules in saved JSON that still lack notes (e.g. whitespace-only). */
export function getModulesMissingNotesFromJson(modules = []) {
  return (modules || [])
    .filter((m) => m?.type && CLASS_LOG_MODULE_TYPES.includes(m.type))
    .filter((m) => !hasNonEmptyText(m.notes))
    .map((m) => m.type);
}

export function validateClassLogBeforeSave(classLogJson) {
  if (Number(classLogJson?.version) === 2) {
    const missing = (classLogJson?.coveredModules || [])
      .filter((m) => m?.module_id)
      .filter((m) => !lmsActivityIdsFromEntry(m).length)
      .map((m) => String(m.module_id));
    if (missing.length > 0) {
      return { ok: false, message: 'Select at least one activity for each selected module.' };
    }
  } else {
    const missing = getModulesMissingNotesFromJson(classLogJson?.coveredModules);
    if (missing.length > 0) {
      return {
        ok: false,
        message:
          'Enter notes for each selected module (e.g. meditation module 1). Empty or only-spaces text is not allowed.',
      };
    }
  }
  if (!isClassLogComplete(classLogJson)) {
    return {
      ok: false,
      message: 'Select at least one module with activities or add other notes.',
    };
  }
  return { ok: true };
}

export function validateNextPlanBeforeSave(nextPlanJson) {
  if (Number(nextPlanJson?.version) === 2) {
    const missing = (nextPlanJson?.plannedModules || [])
      .filter((m) => m?.module_id)
      .filter((m) => !lmsActivityIdsFromEntry(m).length)
      .map((m) => String(m.module_id));
    if (missing.length > 0) {
      return { ok: false, message: 'Select at least one activity for each selected module in the next class plan.' };
    }
  } else {
    const missing = getModulesMissingNotesFromJson(nextPlanJson?.plannedModules);
    if (missing.length > 0) {
      return {
        ok: false,
        message:
          'Enter notes for each selected module in the next class plan. Empty or only-spaces text is not allowed.',
      };
    }
  }
  return { ok: true };
}

/** Human-readable activity list for LMS v2 module entries. */
export function formatLmsActivities(m) {
  const names = (m.activity_names || []).filter((n) => hasNonEmptyText(n)).map(String);
  if (names.length) return names.join(', ');
  const ids = lmsActivityIdsFromEntry(m);
  return ids.join(', ');
}

/** One-line summary for next-class plans (v1 and LMS v2). */
export function buildNextPlanSummary(nextPlanJson) {
  const normalized = normalizeNextPlanJson(nextPlanJson);
  const parts = [];
  if (normalized.plannedModules.length > 0) {
    if (Number(normalized.version) === 2) {
      parts.push(
        ...normalized.plannedModules.map((m) => {
          const label = m.module_name || m.module_id || 'Module';
          const level = m.level ? ` L${m.level}` : '';
          const activity = formatLmsActivities(m);
          return activity ? `${label}${level}: ${activity}` : `${label}${level}`;
        })
      );
    } else {
      parts.push(
        ...normalized.plannedModules.map((m) => {
          const label = MODULE_LABELS[m.type] || m.type;
          const level = m.level ? ` L${m.level}` : '';
          const notes = m.notes ? `: ${m.notes}` : '';
          return `${label}${level}${notes}`;
        })
      );
    }
  }
  if (hasNonEmptyText(normalized.legacyText)) {
    parts.push(String(normalized.legacyText).trim());
  }
  return parts.join(' | ');
}

/** Read-only text for stored v1 class logs (DB unchanged). */
export function getLegacyClassLogPreviewText(classLogJson, legacyBatchNotes) {
  if (classLogJson && typeof classLogJson === 'object' && Number(classLogJson.version) === 2) {
    return null;
  }
  const normalized = normalizeClassLogJson(classLogJson, legacyBatchNotes);
  const parts = (normalized.coveredModules || []).map((m) => {
    const label = MODULE_LABELS[m.type] || m.type;
    const level = m.level ? ` L${m.level}` : '';
    const notes = m.notes ? `: ${m.notes}` : '';
    return `${label}${level}${notes}`;
  });
  if (hasNonEmptyText(normalized.miscRemarks)) {
    parts.push(`Misc: ${String(normalized.miscRemarks).trim()}`);
  }
  if (parts.length > 0) return parts.join(' | ');
  const legacyOnly = String(legacyBatchNotes || '').trim();
  return legacyOnly || null;
}

/** Read-only text for stored v1 next-class plans (DB unchanged). */
export function getLegacyNextPlanPreviewText(nextPlanJson, legacyWeeklyPlan) {
  if (nextPlanJson && typeof nextPlanJson === 'object' && Number(nextPlanJson.version) === 2) {
    return null;
  }
  const normalized = normalizeNextPlanJson(nextPlanJson, legacyWeeklyPlan);
  const parts = (normalized.plannedModules || []).map((m) => {
    const label = MODULE_LABELS[m.type] || m.type;
    const level = m.level ? ` L${m.level}` : '';
    const notes = m.notes ? `: ${m.notes}` : '';
    return `${label}${level}${notes}`;
  });
  if (hasNonEmptyText(normalized.legacyText)) {
    parts.push(String(normalized.legacyText).trim());
  }
  if (parts.length > 0) return parts.join(' | ');
  const legacyOnly = String(legacyWeeklyPlan || '').trim();
  return legacyOnly || null;
}
